// apps/client/src/components/help/HelpDrawer.tsx
// Right-side slide-in drawer rendering PAGE_HELP[pageKey] content.
// REM-anchored width per docs/UI_UX_STANDARDS.md Rule 4.
//
// IMPORTANT: rendered via createPortal to document.body so the drawer
// escapes any ancestor stacking context (transform / filter / contain on
// AppLayout's <main> or sidebar). Without the portal, stat cards bled
// through the drawer despite z-50 (verified on stage 2026-05-12).

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight } from 'lucide-react';
import type { PageHelpKey } from '../../config/helpContent';
import { PAGE_HELP } from '../../config/helpContent';

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
  pageKey: PageHelpKey;
  /** Fired on close. Receives whether user scrolled to bottom. */
  onCloseTelemetry?: (info: { scrolledToBottom: boolean }) => void;
  /** Telemetry callback when a "Use case" item is clicked. */
  onUseCaseClick?: (index: number) => void;
  /** Telemetry callback when a related-page link is clicked. */
  onRelatedPageClick?: (targetPageKey: string) => void;
}

/**
 * Slide-in drawer from the right. Width contract:
 *   - `< 40rem` (mobile):  w-full
 *   - `40-80rem` (tablet): sm:max-w-md (28rem)
 *   - `≥ 80rem` (desktop): xl:max-w-lg (32rem)
 *
 * Scales with user font-size and browser zoom — never hardcoded px.
 */
export const HelpDrawer: React.FC<HelpDrawerProps> = ({
  open,
  onClose,
  pageKey,
  onCloseTelemetry,
  onUseCaseClick,
  onRelatedPageClick,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerWasFocusedRef = useRef<HTMLElement | null>(null);
  const scrolledToBottomRef = useRef<boolean>(false);

  const help = PAGE_HELP[pageKey];

  // Esc closes; track which element opened us to restore focus.
  useEffect(() => {
    if (!open) return;
    triggerWasFocusedRef.current = (document.activeElement as HTMLElement) ?? null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);

    // Scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the drawer for keyboard nav
    drawerRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the trigger
      triggerWasFocusedRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    onCloseTelemetry?.({ scrolledToBottom: scrolledToBottomRef.current });
    onClose();
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      scrolledToBottomRef.current = true;
    }
  };

  if (!open) return null;
  if (typeof document === 'undefined') return null; // SSR guard

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-drawer-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-md xl:max-w-lg bg-gray-800 border-l border-gray-700 shadow-2xl flex flex-col focus:outline-none"
      >
        {/* Accent rail */}
        <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600" />

        {/* Header */}
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-700">
          <div className="min-w-0">
            <h2
              id="help-drawer-title"
              className="text-lg font-semibold text-white leading-tight"
            >
              {help.title}
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              What this page does · How to read the numbers
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close help"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {/* Body — scrollable */}
        <div
          className="flex-1 overflow-y-auto px-5 py-5 space-y-6 text-sm text-gray-200 leading-relaxed"
          onScroll={handleScroll}
        >
          {/* Verbatim prose sections. Used by entries whose copy is frozen
              (see the loanBookValue note in helpContent.ts). Rendered in place
              of overview / use cases / card glossary, never alongside. */}
          {help.sections && help.sections.length > 0 && (
            <>
              {help.sections.map((s) => (
                <section key={s.key} data-testid={`help-section-${s.key}`}>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                    {s.heading}
                  </h3>
                  <p>{s.body}</p>
                </section>
              ))}
            </>
          )}

          {/* Overview */}
          {help.overview && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                Overview
              </h3>
              <p>{help.overview}</p>
            </section>
          )}

          {/* Use cases */}
          {help.useCases && help.useCases.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                When to use this page
              </h3>
              <ul className="space-y-1.5">
                {help.useCases.map((useCase, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 cursor-default"
                    onClick={() => onUseCaseClick?.(idx)}
                  >
                    <span className="text-blue-400 mt-1 flex-shrink-0" aria-hidden="true">
                      •
                    </span>
                    <span>{useCase}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Card glossary */}
          {help.cards && help.cards.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                What each card means
              </h3>
              <dl className="space-y-3">
                {help.cards.map((card) => (
                  <div
                    key={card.key}
                    className="bg-gray-900/60 rounded-md p-3 border border-gray-700/60"
                  >
                    <dt className="text-sm font-semibold text-white">{card.title}</dt>
                    <dd className="text-sm text-gray-300 mt-1">{card.definition}</dd>
                    {card.formula && (
                      <dd className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-700/60">
                        <span className="font-medium text-gray-300">Formula: </span>
                        {card.formula}
                      </dd>
                    )}
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* Industry benchmark */}
          {help.industryBenchmark && help.industryBenchmark.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                Industry benchmark
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  {help.industryBenchmark.map((b) => (
                    <tr key={b.label} className="border-b border-gray-700/40 last:border-b-0">
                      <td className="py-2 pr-3 text-gray-300 font-medium align-top whitespace-nowrap">
                        {b.label}
                      </td>
                      <td className="py-2 pr-3 text-white font-mono align-top whitespace-nowrap">
                        {b.value}
                      </td>
                      <td className="py-2 text-gray-400 align-top">{b.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Methodology */}
          {help.methodology && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                Methodology
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed">{help.methodology}</p>
            </section>
          )}

          {/* Related pages */}
          {help.relatedPages && help.relatedPages.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                Related reports
              </h3>
              <ul className="space-y-1.5">
                {help.relatedPages.map((rel) => (
                  <li key={rel.href}>
                    <a
                      href={rel.href}
                      onClick={() => onRelatedPageClick?.(rel.href)}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                    >
                      {rel.label}
                      <ArrowRight size={14} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Footer */}
        <footer className="px-5 py-3 border-t border-gray-700 text-xs text-gray-500">
          Last updated: {help.lastUpdated}
        </footer>
      </aside>
    </>,
    document.body,
  );
};

export default HelpDrawer;
